import { z } from 'zod'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import ColorField from 'app/frontend/base/components/form-fields/ColorField'
import LegacyApplicationFormPreview from '../LegacyApplicationFormPreview'
import { Link } from 'react-router-dom'
import ResetLegacyFormColorsButton from '../ResetLegacyFormColorsButton'
import { Label } from '@/components/ui/label'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import RichTextField from 'app/frontend/base/components/form-fields/RichTextField'
import { uploadFile } from 'app/frontend/base/api/team'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import { zSetting } from 'app/frontend/base/helpers/forms'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import QuestionsTable from '../QuestionsTable'
import { questionTypes } from 'app/frontend/types/custom/QuestionType'
import LinkButton from '@/components/ui/link-button'

export const formSchema = z.object({
  recruitmentDetails: z.object({
    introduction: z.string().optional().or(z.null()),
    logo: z.string().optional().or(z.null()),
    submitted_introduction: z.string().optional().or(z.null()),
    discord_link: z
      .union([
        z.string().regex(/^https:\/\/discord\.gg\/[a-zA-Z0-9]+$/, {
          message:
            'Enter a valid Discord invite link (https://discord.gg/...), or leave empty',
        }),
        z.literal(''),
        z.null(),
      ])
      .optional(),
    css_background: z.string().optional().or(z.null()),
    css_body: z.string().optional().or(z.null()),
    css_headers: z.string().optional().or(z.null()),
    css_text: z.string().optional().or(z.null()),
    css_inputs: z.string().optional().or(z.null()),
    css_header_bar: z.string().optional().or(z.null()),
    show_country: z.boolean().optional().or(z.null()),
    show_age: z.boolean().optional().or(z.null()),
  }),
  settings: z.object({
    recruitmentInternalNotesVisibility: zSetting(
      z.enum(['full_access', 'readonly', 'none']),
    ),
    recruitmentMessagesVisibility: zSetting(
      z.enum(['full_access', 'readonly', 'none']),
    ),
    recruitmentApplicationHistoryVisibility: zSetting(
      z.enum(['entire_team', 'admins_only']),
    ),
    displayApplyButtonInOverview: zSetting(z.boolean()),
  }),
  questions_attributes: z.array(
    z.object({
      id: z.number().optional().or(z.null()),
      questionType: z.enum(questionTypes),
      description: z.string(),
      required: z.boolean(),
      order: z.number().optional(),
      _destroy: z.boolean().optional(),
      question_options_attributes: z
        .array(
          z.object({
            id: z.number().optional().or(z.null()),
            option: z.string(),
            _destroy: z.boolean().optional(),
          }),
        )
        .optional(),
    }),
  ),
  resetToDefaultAt: z.string().optional(),
})

type TeamWithQuestions = wowaudit.Team & {
  questions_attributes: (wowaudit.Question & {
    _destroy?: boolean
    question_options_attributes?: (wowaudit.QuestionOption & {
      _destroy?: boolean
    })[]
  })[]
}

const RecruitmentSection = () => {
  const { team } = useTeam()

  return (
    <>
      <FormWrapper<TeamWithQuestions>
        key={team.id}
        formSchema={formSchema}
        entityType='team'
        entity={{
          ...team,
          questions_attributes:
            team.questions?.map((q) => ({
              ...q,
              question_options_attributes: q.questionOptions ?? [],
            })) ?? [],
        }}
        onSave={async (form, data) => {
          form.reset({
            ...data,
            questions_attributes:
              data.questions?.map((q) => ({
                ...q,
                question_options_attributes: q.questionOptions ?? [],
              })) ?? [],
          })
        }}
        transformValues={(values) => ({
          ...values,
          questions_attributes: values.questions_attributes.map(
            (
              q: wowaudit.Question & {
                _destroy?: boolean
                question_options_attributes?: (wowaudit.QuestionOption & {
                  _destroy?: boolean
                })[]
              },
              i: number,
            ) => ({
              ...q,
              order: i,
            }),
          ),
        })}
      >
        <div className='flex items-center justify-between gap-4 mb-4'>
          <h5>Custom questions</h5>

          <LinkButton
            to={`${team.path}/apply`}
            target='_blank'
            size='sm'
            rel='noreferrer'
          >
            View form
          </LinkButton>
        </div>

        <QuestionsTable />

        <CheckboxField name='recruitmentDetails.show_country' />
        <CheckboxField name='recruitmentDetails.show_age' />

        <RichTextField
          name='recruitmentDetails.introduction'
          uploader={async (file) => (await uploadFile(team.id, file)).url}
        />

        <TextField name='recruitmentDetails.submitted_introduction' isMulti />

        <TextField name='recruitmentDetails.discord_link' trimOnBlur />

        <SettingField name='recruitmentInternalNotesVisibility' />
        <SettingField name='recruitmentMessagesVisibility' />
        <SettingField name='recruitmentApplicationHistoryVisibility' />
        <SettingField name='displayApplyButtonInOverview' layout='box' />

        <h4 className='mt-12'>Standalone form</h4>
        <p className='mb-6'>
          In addition to the embedded form, you can also use a customizable
          standalone form. It can be found{' '}
          <Link
            to={`https://apply.wowaudit.com${team.path}`}
            target='_blank'
            rel='noreferrer'
            className='text-primary hover:text-primary/90'
          >
            here
          </Link>
          .
        </p>

        <TextField
          name='recruitmentDetails.logo'
          type='url'
          pattern='.*\.(svg|jpe?g|png|webp)(\?.*)?$'
        />

        <div className='flex gap-4 max-sm:flex-wrap'>
          <div className='w-full space-y-1'>
            <Label>Preview</Label>
            <LegacyApplicationFormPreview />
          </div>

          <div className='whitespace-nowrap flex flex-col gap-4'>
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_background'
              originalValue={team.recruitmentDetails?.css_background ?? null}
              customDescription=''
              allowImageUrl={true}
            />
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_body'
              originalValue={team.recruitmentDetails?.css_body ?? null}
              customDescription=''
              allowImageUrl={true}
            />
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_headers'
              originalValue={team.recruitmentDetails?.css_headers ?? null}
              customDescription=''
            />
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_text'
              originalValue={team.recruitmentDetails?.css_text ?? null}
              customDescription=''
            />
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_inputs'
              originalValue={team.recruitmentDetails?.css_inputs ?? null}
              customDescription=''
            />
            <ColorField
              layout='stacked'
              name='recruitmentDetails.css_header_bar'
              originalValue={team.recruitmentDetails?.css_header_bar ?? null}
              customDescription=''
            />
            <ResetLegacyFormColorsButton />
          </div>
        </div>
      </FormWrapper>
    </>
  )
}

export default RecruitmentSection
